import { Option, Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import { ActionType, ColumnDataType } from 'components/elements/table/type';
import Text from 'components/elements/text';
import { SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  ActivitySaleAddOnsItemFormType,
  ActivitySaleFormType,
} from '../form-type';
import { addNewAddOnsItem } from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
}

function useGetActivityAddOnsTableList({ parentIndex }) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const { control } = useFormContext<ActivitySaleFormType>();
  const name = `activitySales.${parentIndex}.activitySaleGuests` as const;

  const guests = useWatch({
    name,
    control,
  });

  const columns = React.useMemo(() => {
    const guestList: Option[] = guests
      .filter((guest) => !!guest.guestId)
      .map((guest) => {
        return {
          id: guest.guestId,
          label: guest.guestName,
          extra: guest,
        };
      });
    return [
      {
        header: t('common:name'),
        accessorKey: 'guestId',
        minSize: 100,
        dataType: ColumnDataType.select,
        options: guestList,
        required: true,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 500,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:price'),
        accessorKey: 'price',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
      ...(editable
        ? [
            {
              header: '',
              accessorKey: 'delete',
              minSize: 60,
              size: 60,
              maxSize: 60,
              dataType: ColumnDataType.delete,
            },
          ]
        : []),
    ];
  }, [editable, guests, t]);

  return columns;
}

export default function ActivityAddOnsTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const name = `activitySales.${props.parentIndex}.activitySaleAddOns` as const;

  const { control, trigger, getValues } = useFormContext();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetActivityAddOnsTableList({
    parentIndex: props.parentIndex,
  });

  const dispatch = React.useCallback(
    (action) => {
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof ActivitySaleAddOnsItemFormType;
      const currentRowIndex = action.rowIndex as number;
      const addOns = getValues(name);
      const addOn = addOns[currentRowIndex];
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<ActivitySaleAddOnsItemFormType> = () => {
        if (columnId === 'guestId') {
          const guestName = extra?.[0]?.label as string;
          return { guestName };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewAddOnsItem());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...addOn,
          [columnId]: value,
          ...otherVal(),
        });
        trigger(name);
      }
    },
    [append, getValues, name, remove, trigger, update],
  );

  if (fields.length || editable) {
    return (
      <>
        <Text variant="ParagraphSmall">{t('ticketSales:add_ons')}</Text>
        <SeparatorVertical $margin={12} />
        <div style={{ width: '100%' }}>
          <TableInput
            columns={columns}
            data={fields}
            dispatch={dispatch}
            disabled={!editable}
            name={name}
            uniqueRowKey="customId"
          />
        </div>
      </>
    );
  }

  return null;
}
